import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { Constants } from "../constants/constants";
import { rehydrateFromActiveRow } from "../services/rowRehydrator";
import { clearRoles, writeRoles } from "../services/writerService";
const RoleSelectionContext = createContext(undefined);
export const RoleSelectionProvider = ({ rolesList, children }) => {
    const [selectedRoleIds, setSelectedRoleIds] = useState([]);
    const [lastRoleMessage, setLastRoleMessage] = useState(null);
    const lastAddressRef = useRef(null);
    // Guard flag: when true, the onChanged sync handler is suppressed to prevent
    // cascading loops between columns F and K during programmatic writes/clears.
    const isSyncingRef = useRef(false);
    // Avoid spamming console if the polling hook hits transient Excel/Office errors.
    const pollErrorLoggedRef = useRef(false);
    /**
     * Worksheet change handler to keep Role Name (F) and Role ID (K) in sync.
     *
     * Behavior (manual edits only):
     * - If user clears Role Name (F), auto-clear Role ID (K)
     * - If user clears Role ID (K), auto-clear Role Name (F)
     *
     * IMPORTANT: This must never recurse (F->K triggers K->F triggers ...),
     * so we use isSyncingRef to ignore events caused by our own writes.
     */
    const handleRoleSheetChanged = async (event) => {
        // Skip if a programmatic write/clear (or our own sync) is in progress.
        if (isSyncingRef.current)
            return;
        try {
            await Excel.run(async (innerCtx) => {
                const ws = innerCtx.workbook.worksheets.getActiveWorksheet();
                const changedRange = ws.getRange(event.address);
                changedRange.load(["columnIndex", "rowIndex", "rowCount", "values"]);
                await innerCtx.sync();
                const col = changedRange.columnIndex;
                const rowStart = changedRange.rowIndex;
                const rowCount = changedRange.rowCount;
                const isRoleNameCol = col === Constants.ROLE_COLUMNS.ROLE_NAME;
                const isRoleIdCol = col === Constants.ROLE_COLUMNS.ROLE_ID;
                if (!isRoleNameCol && !isRoleIdCol)
                    return;
                // Lock while we apply the paired clear so resulting onChanged events are ignored.
                isSyncingRef.current = true;
                if (isRoleNameCol) {
                    for (let i = 0; i < rowCount; i++) {
                        const val = String(changedRange.values[i]?.[0] || "").trim();
                        if (!val) {
                            ws.getCell(rowStart + i, Constants.ROLE_COLUMNS.ROLE_ID).values = [[""]];
                        }
                    }
                    await innerCtx.sync();
                }
                if (isRoleIdCol) {
                    for (let i = 0; i < rowCount; i++) {
                        const val = String(changedRange.values[i]?.[0] || "").trim();
                        if (!val) {
                            ws.getCell(rowStart + i, Constants.ROLE_COLUMNS.ROLE_NAME).values = [[""]];
                        }
                    }
                    await innerCtx.sync();
                }
            });
        }
        catch (e) {
            console.warn("Role sync handler error", e);
        }
        finally {
            // Let Excel finish firing any queued events from the paired clear, then unlock.
            setTimeout(() => {
                isSyncingRef.current = false;
            }, 250);
        }
    };
    async function writeSelectedRoles() {
        console.log("writeSelectedRoles called", { rolesList: !!rolesList, selectedRoleIds });
        if (!rolesList) {
            console.log("No rolesList available, returning");
            return;
        }
        // Convert selected role IDs -> role names (filter removes missing IDs safely).
        const roleNames = selectedRoleIds
            .map((id) => rolesList.byRoleId[id]?.name)
            .filter((name) => Boolean(name));
        console.log("Role data prepared", { selectedRoleIds, roleNames });
        // IMPORTANT: writeRoles updates many cells; without this guard the worksheet onChanged
        // handler below can re-enter and create a cascade that freezes the add-in.
        isSyncingRef.current = true;
        try {
            const result = await writeRoles(roleNames, selectedRoleIds);
            console.log("writeRoles result", result);
            setLastRoleMessage(result.message + ` (rows ${result.rows.join(", ")})`);
        }
        catch (error) {
            console.error("Error writing roles:", error);
            setLastRoleMessage("Error writing roles: " + (error instanceof Error ? error.message : String(error)));
        }
        finally {
            // Small delay lets any pending onChanged events drain before re-enabling the handler.
            setTimeout(() => {
                isSyncingRef.current = false;
            }, 500);
        }
    }
    async function clearSelectedRoles() {
        // IMPORTANT: clearRoles updates ranges; suppress onChanged handling to prevent recursion.
        isSyncingRef.current = true;
        try {
            const result = await clearRoles();
            setSelectedRoleIds([]);
            setLastRoleMessage(result.message + ` (row ${result.row})`);
        }
        finally {
            // Small delay lets any pending onChanged events drain before re-enabling the handler.
            setTimeout(() => {
                isSyncingRef.current = false;
            }, 500);
        }
    }
    async function rehydrateRolesFromRow() {
        try {
            const result = await rehydrateFromActiveRow();
            setSelectedRoleIds(result.roleIds);
            setLastRoleMessage(`Roles rehydrated from row ${result.sourceRow}`);
        }
        catch (e) {
            console.warn("Role rehydrate failed", e);
            setLastRoleMessage("Role rehydrate failed");
        }
    }
    // Auto-clear role selection when Excel active cell changes
    useEffect(() => {
        if (!Office?.context?.workbook?.getActiveCell)
            return;
        let disposed = false;
        const hook = async () => {
            try {
                await Excel.run(async (ctx) => {
                    const range = ctx.workbook.getSelectedRange();
                    range.load("address");
                    await ctx.sync();
                    const addr = range.address;
                    if (lastAddressRef.current && lastAddressRef.current !== addr) {
                        if (selectedRoleIds.length) {
                            setSelectedRoleIds([]);
                            setLastRoleMessage("Role selection cleared (active cell changed)");
                        }
                    }
                    lastAddressRef.current = addr;
                });
            }
            catch (e) {
                // Non-fatal: Excel APIs can throw during workbook transitions or if the host isn't ready.
                // Log once to help debugging without flooding the console every 1.2s.
                if (!pollErrorLoggedRef.current) {
                    pollErrorLoggedRef.current = true;
                    console.warn("Role selection poll failed (will be suppressed)", e);
                }
            }
            if (!disposed) {
                setTimeout(hook, 1200);
            }
        };
        hook();
        return () => {
            disposed = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRoleIds.length]);
    // Sync watcher: monitor column F & K for manual edits.
    // If user manually clears role name (F), auto-clear role ID (K) and vice versa.
    useEffect(() => {
        const registerHandler = async () => {
            try {
                await Excel.run(async (ctx) => {
                    const sheet = ctx.workbook.worksheets.getActiveWorksheet();
                    sheet.onChanged.add(handleRoleSheetChanged);
                    await ctx.sync();
                });
            }
            catch (e) {
                console.warn("Failed to register role sync handler", e);
            }
        };
        registerHandler();
        return () => {
            // Event handlers are cleaned up when the context is disposed
        };
    }, []);
    const value = useMemo(() => ({
        rolesList,
        selectedRoleIds,
        setRoleIds: setSelectedRoleIds,
        writeSelectedRoles,
        rehydrateRolesFromRow,
        clearSelectedRoles,
        lastRoleMessage,
    }), [selectedRoleIds, rolesList, lastRoleMessage]);
    return _jsx(RoleSelectionContext.Provider, { value: value, children: children });
};
export function useRoleSelectionContext() {
    const ctx = useContext(RoleSelectionContext);
    if (!ctx)
        throw new Error("useRoleSelectionContext must be used within RoleSelectionProvider");
    return ctx;
}
